import _ from 'lodash';
import omit from 'lodash/omit';
import moment from 'moment';

import type { ActivityRecord } from '@appTypes/esb';
import {
  activityTypeForActivityTypeId,
  advancementStatuses,
  dtoDateFormat,
  historyItemsTypes,
} from '@modules/shared/constants';
import { intl } from '@modules/shared/localization/translate';
import { personNameBuilder } from '@modules/utils';
import AdvancementStatus from '@shared/components/AdvancementStatus';

import type { RosterScout } from '../duck/types';
import { updatedDateFilters } from './constants';
import { getLastUpdatedDate } from './helpers';
import { getFilterableAdvancementStatus } from './utils';

const isPendingItem = (status: string) =>
  status === advancementStatuses.COMPLETED ||
  status === advancementStatuses.COUNSELOR_APPROVED;

const createMemberItems = (
  registeredMembers: ActivityRecord['registeredYouths'],
  activityType: ActivityRecord['activityType'],
  activity: Omit<
    ActivityRecord,
    | 'id'
    | 'name'
    | 'registeredYouths'
    | 'registeredAdults'
    | 'nonRegisteredOrgParticipants'
  >,
  activityId: ActivityRecord['id'],
  activityName: ActivityRecord['name'],
) => {
  const getActivityStatus = ({ isApproved }: { isApproved: boolean }) =>
    isApproved ? advancementStatuses.APPROVED : advancementStatuses.COMPLETED;

  const getActivityDescription = (name: string, activityType: string) =>
    intl.formatMessage(
      {
        id: `advancement.AdvancementHistory.activityItemDescription.${activityType}`,
      },
      { name },
    );

  const getActivityAdvancementName = (activityType: string) =>
    intl.formatMessage({
      id: `advancement.PendingItems.activity.${activityType}`,
    });

  return registeredMembers.map(({ activityValues, ...member }) => {
    const leaderApprovedUserId = member.leaderApprovedId;
    const status = getActivityStatus(member);
    const isAdvancementPending = isPendingItem(status);
    return {
      ...member,
      ...activity,
      leaderApprovedUserId,
      key: `${activityId}-${member.userId}-${member.memberId}`,
      activityRecords: [...activityValues],
      type: historyItemsTypes.ACTIVITY,
      isAdvancementPending,
      activityId,
      activityName,
      activityType,
      name: personNameBuilder.short(member),
      status,
      statusLabel: AdvancementStatus.getTranslation(status),
      filterableStatus: getFilterableAdvancementStatus(status),
      date: activity.startDateTime,
      description: getActivityDescription(activityName, activityType),
      advancementName: getActivityAdvancementName(activityType),
    };
  });
};

type ActivityTypeId = keyof typeof activityTypeForActivityTypeId;

export function denormalizeActivityItems(activityDto: ActivityRecord) {
  const {
    registeredYouths,
    registeredAdults,
    id: activityId,
    name: activityName,
    ...activity
  } = omit(activityDto, 'nonRegisteredOrgParticipants');
  const activityType =
    activityTypeForActivityTypeId[activity.activityTypeId as ActivityTypeId];
  const youthItems = createMemberItems(
    registeredYouths,
    activityType,
    activity,
    activityId,
    activityName,
  );
  const adultItems = createMemberItems(
    registeredAdults,
    activityType,
    activity,
    activityId,
    activityName,
  );

  return [...youthItems, ...adultItems];
}

export type DenormalizedActivityItem = ReturnType<
  typeof denormalizeActivityItems
>[0];

/**
 * Used for activities query
 */
export function buildCompletionDateParams(updatedDate: string | Date) {
  const completionDateEnd = moment().format(dtoDateFormat);
  const updatedDateFilter =
    updatedDate === updatedDateFilters.ALL
      ? updatedDateFilters.ALL
      : updatedDateFilters.LAST_365_DAYS;
  const completionDateStart =
    getLastUpdatedDate(updatedDateFilter).format(dtoDateFormat);

  return {
    completionDateStart,
    completionDateEnd,
  };
}

export function appendRecordedByInfo(
  activities: DenormalizedActivityItem[],
  adults: RosterScout[],
) {
  return activities.map(activityItem => {
    const { leaderApprovedUserId } = activityItem;
    const recordedBy = adults.find(
      ({ userId }) => leaderApprovedUserId == userId,
    );
    const { firstName, lastName } = recordedBy || {};
    return {
      ...activityItem,
      recordedByFirstName: firstName,
      recordedByLastName: lastName,
      recordedByName: personNameBuilder.short(recordedBy || {}),
    };
  });
}

export function transformActivities(
  activities: ActivityRecord[],
  rosterAdults: RosterScout[],
) {
  const data = _.chain(activities)
    .defaultTo([])
    .map(denormalizeActivityItems)
    .flatten()
    .thru(items => appendRecordedByInfo(items, rosterAdults));

  return data.value();
}
